import styled from '@emotion/styled';
import { forPhoneOnly, Variables } from '../../base';
import { AttachmentButton } from '../attachment-button';

interface IEntry {
    note?: boolean;
    own?: boolean;
}

export const History = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    gap: 8px;
`;

export const CommentsHeader = styled.div`
    font-size: 11px;
    font-weight: ${Variables.FontWeight.semibold};
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${Variables.ThemeLight.TextMild};
    padding: 20px 24px 0;
`;

export const HistoryBody = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    gap: 14px;
    padding: 0 24px 8px;
    margin: 0;
`;

export const HistoryEntry = styled.div`
    position: relative;
    display: flex;
    flex-direction: ${(props: IEntry): string => (props.own ? 'row-reverse' : 'row')};
    align-items: flex-end;
    gap: 10px;
    font-size: 14px;
    letter-spacing: 0;

    ${forPhoneOnly(`
        gap: 8px;
    `)};
`;

export const HistoryEntryContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-width: 78%;
    align-items: ${(props: { own?: boolean }): string => (props.own ? 'flex-end' : 'flex-start')};
`;

export const HistoryEntryHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 2px;
`;

export const HistoryEntryName = styled.div`
    font-size: 12px;
    font-weight: ${Variables.FontWeight.semibold};
    color: ${Variables.ThemeLight.TextMedium};
`;

export const HistoryEntryTime = styled.div`
    font-size: 11px;
    font-weight: ${Variables.FontWeight.regular};
    color: ${Variables.ThemeLight.TextMild};
`;

export const HistoryNoteIndicator = styled.div`
    display: flex;
`;

const EntryComment = (own?: boolean): string => `
    background: ${own ? Variables.ThemeLight.PrimaryOpacity3 : Variables.ThemeLight.NeutralOpacity2};
    border: 1px solid ${own ? Variables.ThemeLight.PrimaryInverted7 : Variables.ThemeLight.Border2};
    border-radius: ${own ? '10px 10px 3px 10px' : '10px 10px 10px 3px'};
`;

const EntryNote = `
    background: ${Variables.ThemeLight.NeutralOpacity2};
    border: 1px solid ${Variables.ThemeLight.Border2};
    border-radius: 10px;
`;

export const HistoryEntryMessage = styled.div`
    display: block;
    padding: 9px 12px;
    font-size: 14px;
    font-weight: ${Variables.FontWeight.regular};
    line-height: 1.45;
    color: ${Variables.ThemeLight.TextStrong};
    overflow-x: hidden;

    ${(props: IEntry): string => (props.note ? EntryNote : EntryComment(props.own))}

    /* Markdown wraps plain text in <p>; HTML may use <span>/<div> — normalize both */
    p,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        margin: 0;
        padding: 0;
        font-size: inherit;
        font-weight: inherit;
        line-height: inherit;
        color: inherit;
    }

    p + p {
        margin-top: 0.5em;
    }

    ul,
    ol {
        margin: 0;
        padding-left: 1.25em;
    }

    * {
        white-space: pre-wrap;
        max-width: 100%;
    }

    pre,
    code {
        word-wrap: break-word;
        overflow-wrap: break-word;
        white-space: pre-wrap;
    }
`;

export const HistoryEntryAttachment = styled.div`
    padding: 4px 0 0;
`;

/** Compact chip-style attachment in conversation; keeps download/edit/assign/remove actions. */
export const ConversationAttachment = styled(AttachmentButton)`
    && {
        min-width: 0;
        max-width: 260px;
        padding: 5px 8px;
        margin-top: 0;
        background: ${Variables.ThemeLight.Background1};
        border: 1px solid ${Variables.ThemeLight.Border2};
        border-radius: 5px;
        gap: 6px;
    }

    && > div:first-of-type {
        width: 18px;
        height: 18px;
        border-radius: 4px;
    }

    && > div:nth-of-type(2) {
        margin-left: 0;
        font-size: 12px;
    }

    && > div:nth-of-type(2) > div:first-of-type {
        margin-bottom: 0;
        line-height: 1.2;
        font-size: 12px;
        font-weight: ${Variables.FontWeight.medium};
        color: ${Variables.ThemeLight.TextMedium};
    }

    && > button {
        margin-left: 4px;
        width: 20px;
        height: 20px;
    }
`;

export const LoadingText = styled.div`
    font-size: 14px;
    color: ${Variables.Colors.n50};
    font-style: italic;
    padding: 8px 0;
`;
